import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Search, Plus, Bell, User, LogOut, Settings, Menu } from 'lucide-react';
import NotificationBell from './NotificationBell';
import MobileDrawer from './MobileDrawer';
import toast from 'react-hot-toast';

const StickyHeader = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Scroll detection for mobile search bar
  useEffect(() => {
    let timeoutId;
    const handleScroll = () => {
      // Debounce scroll events for performance
      if (timeoutId) clearTimeout(timeoutId);
      
      timeoutId = setTimeout(() => {
        const scrollPosition = window.scrollY;
        setIsScrolled(scrollPosition > 150);
      }, 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleAskQuestion = () => {
    if (user) {
      navigate('/ask');
    } else {
      toast.error('Please log in to continue');
      setTimeout(() => {
        navigate('/login');
      }, 1000);
    }
  };

  const handleNotificationClick = () => {
    if (user) {
      navigate('/notifications');
    } else {
      toast.error('Please log in to continue');
      setTimeout(() => {
        navigate('/login');
      }, 1000);
    }
  };

  const handleProfileClick = () => {
    if (user) {
      navigate(`/profile/${user.username}`);
    } else {
      toast.error('Please log in to continue');
      setTimeout(() => {
        navigate('/login');
      }, 1000);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  // Get user initials for avatar fallback
  const getUserInitials = () => {
    if (!user) return 'U';
    if (user.full_name) {
      const parts = user.full_name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return parts[0][0].toUpperCase();
    }
    return user.username?.[0]?.toUpperCase() || 'U';
  };

  return (
    <>
      {/* Mobile Drawer */}
      <MobileDrawer 
        isOpen={mobileDrawerOpen} 
        onClose={() => setMobileDrawerOpen(false)} 
      />

      {/* Main Header - Sticky */}
      <header className="bg-white shadow-sm fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Left Side: Hamburger (mobile only) + Logo */}
            <div className="flex items-center gap-2">
              {/* Hamburger Menu - Mobile Only */}
              <button
                onClick={() => setMobileDrawerOpen(true)}
                className="md:hidden p-2 -ml-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6" />
              </button>

              {/* Logo */}
              <Link to="/" className="flex items-center flex-shrink-0">
                <span className="text-xl sm:text-2xl font-bold">
                  <span className="text-purple-600">Webo</span>
                  <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Ask</span>
                </span>
              </Link>
            </div>

            {/* Desktop Search Bar - Always Visible */}
            <form 
              onSubmit={handleSearch} 
              className="hidden md:flex flex-1 max-w-2xl mx-8 lg:mx-12"
            >
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search questions, topics, or keywords"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm shadow-sm"
                />
              </div>
            </form>

            {/* Right Side Actions - Always Visible */}
            <div className="flex items-center gap-2 md:gap-3">
              {/* Ask Question Button - Icon Only with Tooltip */}
              <div className="relative group">
                <button
                  onClick={handleAskQuestion}
                  className="flex items-center justify-center w-10 h-10 md:w-11 md:h-11 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all hover:scale-105 active:scale-95"
                  aria-label="Ask a Question"
                  title="Ask a Question"
                >
                  <Plus className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2.5} />
                </button>
                
                {/* Tooltip - Desktop Only */}
                <div className="hidden lg:block absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap pointer-events-none z-50">
                  Ask a Question
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>

              {/* Notification Bell - Always Visible */}
              {user ? (
                <div className="hidden md:block">
                  <NotificationBell />
                </div>
              ) : (
                <button
                  onClick={handleNotificationClick}
                  className="hidden md:flex items-center justify-center w-10 h-10 md:w-11 md:h-11 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                  aria-label="Notifications"
                  title="Notifications"
                >
                  <Bell className="w-5 h-5" />
                </button>
              )}
              
              {/* Mobile Notification Icon */}
              <button
                onClick={handleNotificationClick}
                className="md:hidden flex items-center justify-center w-10 h-10 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                aria-label="Notifications"
                title="Notifications"
              >
                <Bell className="w-5 h-5" />
              </button>

              {/* Profile Icon - Always Visible */}
              {user ? (
                <div className="relative group">
                  <button
                    onClick={handleProfileClick}
                    className="flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 text-white font-semibold text-sm hover:shadow-lg transition-all hover:scale-105"
                    aria-label="Profile"
                    title={user?.full_name || user?.username}
                  >
                    {user?.avatar_url ? (
                      <img 
                        src={user.avatar_url} 
                        alt={user.full_name || user.username}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span>{getUserInitials()}</span>
                    )}
                  </button>

                  {/* Dropdown Menu on Hover - Desktop Only */}
                  <div className="hidden lg:block absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 border border-gray-100 z-50">
                    <Link
                      to={`/profile/${user?.username}`}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 transition-colors"
                    >
                      <User className="w-4 h-4" />
                      Profile
                    </Link>
                    <Link
                      to="/saved"
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                      </svg>
                      Saved
                    </Link>
                    <Link
                      to="/leaderboard"
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 transition-colors"
                    >
                      <span>🏆</span>
                      Leaderboard
                    </Link>
                    {user?.is_admin && (
                      <>
                        <div className="border-t border-gray-100 my-1"></div>
                        <Link
                          to="/admin"
                          className="flex items-center justify-between gap-2 px-4 py-2.5 text-sm font-semibold bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 text-purple-700 rounded-md mx-2 transition-all border border-purple-200"
                        >
                          <div className="flex items-center gap-2">
                            <Settings className="w-4 h-4" />
                            Admin Panel
                          </div>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                        </Link>
                      </>
                    )}
                    <div className="border-t border-gray-100 my-1"></div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors w-full text-left"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleProfileClick}
                  className="flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300 transition-all hover:scale-105"
                  aria-label="Profile"
                  title="Profile"
                >
                  <User className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Sticky Search Bar - Appears on Scroll */}
      <div
        className={`md:hidden fixed left-0 right-0 z-40 bg-white/95 backdrop-blur-md shadow-sm transition-all duration-300 ease-in-out ${
          isScrolled ? 'top-14 opacity-100 translate-y-0' : 'top-14 opacity-0 -translate-y-full pointer-events-none'
        }`}
        style={{ willChange: 'transform, opacity' }}
      >
        <div className="max-w-7xl mx-auto px-4 py-2">
          <form onSubmit={handleSearch}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search questions, topics, or keywords"
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm shadow-sm"
              />
            </div>
          </form>
        </div>
      </div>

      {/* Spacer to prevent content from going under fixed header */}
      <div className="h-14 md:h-16"></div>
      
      {/* Additional spacer for mobile search when visible */}
      {isScrolled && <div className="md:hidden h-11"></div>}
    </>
  );
};

export default StickyHeader;
